/**
 * Global Error Handlers
 *
 * Captures uncaught exceptions and unhandled promise rejections
 * that escape React's error boundary and normal try/catch flows.
 * Forwards them to Datadog Logs (via logger).
 *
 * RUM automatically captures these as "source" errors, so we only
 * forward to the logger to avoid duplicate "custom" errors in RUM.
 */

import { logger } from './frontendLogger';

let installed = false;
let rejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;
let errorHandler: ((event: ErrorEvent) => void) | null = null;

export const setupGlobalErrorHandlers = (): void => {
  if (installed) return;

  rejectionHandler = (event: PromiseRejectionEvent) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    logger.error(`Unhandled promise rejection: ${error.message}`, {
      stack: error.stack,
      type: 'unhandledrejection',
    });
  };

  errorHandler = (event: ErrorEvent) => {
    if (event.error) {
      logger.error(`Uncaught error: ${event.error.message}`, {
        stack: event.error.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        type: 'uncaught',
      });
      return;
    }

    // Cross-origin script errors (or other cases) where event.error is null
    // but the ErrorEvent still carries useful diagnostics.
    logger.error(`Uncaught error: ${event.message || 'Unknown error'}`, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      type: 'uncaught',
    });
  };

  window.addEventListener('unhandledrejection', rejectionHandler);
  window.addEventListener('error', errorHandler);

  installed = true;
};

/**
 * Removes the global error listeners and resets state.
 * Safe to call when not installed (no-op).
 */
export const teardownGlobalErrorHandlers = (): void => {
  if (!installed) return;

  if (rejectionHandler) {
    window.removeEventListener('unhandledrejection', rejectionHandler);
    rejectionHandler = null;
  }

  if (errorHandler) {
    window.removeEventListener('error', errorHandler);
    errorHandler = null;
  }

  installed = false;
};

export const isGlobalErrorHandlersInstalled = (): boolean => installed;
